import React, {Component} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {withRouter} from 'react-router-dom';

import {isDefined} from '@stubhub/general-utils';
import {connect} from '@stubhub/react-store-provider';

import controller from './controller';
import getSearchParams from './getSearchParams';

function updateUrl(history, {query = {}, push}, defaults = {}) {
  const queryKeys = Object.keys(query);
  const currentQuery = getSearchParams(history.location.search);
  const newParams = queryKeys.reduce((result, queryKey) => {
    const queryValue = query[queryKey];
    const defaultValue = defaults[queryKey];
    const hasDefaultValue = isDefined(defaultValue);
    if (isDefined(queryValue)) {
      const newValue = Array.isArray(queryValue) ? queryValue.join(',') : queryValue;
      if (hasDefaultValue) {
        if (defaultValue === newValue) {
          delete currentQuery[queryKey];
        } else {
          result[queryKey] = newValue;
        }
      } else if (newValue) {
        result[queryKey] = newValue;
      } else {
        delete currentQuery[queryKey];
      }
    } else if (hasDefaultValue) {
      result[queryKey] = defaultValue;
    } else {
      delete currentQuery[queryKey];
    }

    return result;
  }, {});
  const newQueryString = `?${queryString.stringify({...currentQuery, ...newParams})}`;
  push ? history.push(newQueryString) : history.replace(newQueryString);
}

function queryParamsWrapper(mapping) {
  function mapQueryToProps(query) {
    const propKeys = Object.keys(mapping);

    return propKeys.reduce((result, propKey) => {
      const queryKey = mapping[propKey];
      if (queryKey) {
        result[propKey] = query[queryKey];
      }

      return result;
    }, {});
  }

  function mapPropsToQuery(props) {
    const propKeys = Object.keys(mapping);

    return propKeys.reduce((result, propKey) => {
      const queryKey = mapping[propKey];
      if (queryKey) {
        result[queryKey] = props[propKey];
      }

      return result;
    }, {});
  }

  function query(WrappedComp) {
    // Const origSetState = WrappedComp.prototype.setState;
    @connect(controller)
    class QueryMapper extends Component {
      state = {
        query: {},
      };

      constructor(props) {
        super(props);
        this.state = {
          query: mapQueryToProps(props.query),
        };
      }

      updateQuery = (query, push) => {
        const {history, queryDefaults} = this.props;
        const {query: stateQuery} = this.state;
        const newQuery = query && Object.keys(query).length > 0 ? mapPropsToQuery({...stateQuery, ...query}) : {};
        updateUrl(
          history,
          {
            push,
            query: newQuery,
          },
          queryDefaults
        );
      };

      clearOwnQuery = (defaultValues = {}) => {
        const {history, queryDefaults} = this.props;
        const updatedQuery = Object.keys(mapping).reduce((result, propKey) => {
          const queryKey = mapping[propKey];
          result[queryKey] = defaultValues?.[queryKey] || queryDefaults[queryKey];

          return result;
        }, {});
        updateUrl(
          history,
          {
            query: updatedQuery,
          },
          queryDefaults
        );
      };

      getQuery = (props = this.props) => {
        return Object.keys(mapping).reduce((result, propKey) => {
          result[propKey] = props?.[propKey];

          return result;
        }, {});
      };

      isQueryUpdated = (currentProps, nextProps) => {
        const propKeys = Object.keys(mapping);
        for (let index = 0; index < propKeys.length; index++) {
          const propKey = propKeys[index];
          const newPropValue = nextProps?.[propKey];
          if (newPropValue !== currentProps?.[propKey]) {
            return true;
          }
        }

        return false;
      };

      UNSAFE_componentWillReceiveProps(nextProps) {
        let newQueryState = {};
        const {query} = this.props;
        if (query !== nextProps.query) {
          newQueryState = mapQueryToProps(nextProps.query);
          this.setState({
            query: newQueryState,
          });
        }
      }

      render() {
        // Don't want query prop being passed down.
        const {query, ...otherProps} = this.props;
        const {query: stateQuery} = this.state;

        return (
          <WrappedComp
            {...otherProps}
            {
              ...stateQuery /* Mapped query params */
            }
            updateQuery={this.updateQuery}
            isQueryUpdated={this.isQueryUpdated}
            getQuery={this.getQuery}
            clearOwnQuery={this.clearOwnQuery}
          />
        );
      }
    }

    QueryMapper.propTypes = {
      /** The router history object */
      history: PropTypes.object,
      queryDefaults: PropTypes.object,
      query: PropTypes.object,
    };

    QueryMapper.defaultProps = {
      query: {},
    };

    return withRouter(QueryMapper);
  }

  return query;
}

export default queryParamsWrapper;
